/**
 * Big Moments Engine
 * ------------------
 * Pure, signal-driven detector that turns the EXISTING match telemetry
 * (score deltas, minute, danger flag, momentum) into discrete BIG MOMENTS:
 *   goal · penalty · var · red_card · comeback · extreme_pressure ·
 *   last_minute · dramatic_draw
 *
 * No new polling, no new realtime — consumers pass in whatever they
 * already render. The engine throttles + dedupes, then publishes via
 * window CustomEvent("vs:big-moment") so any UI surface (flash overlay,
 * prediction card, danger play, polls) can react without coupling.
 */
import { trackEvent } from "@/lib/audit";

export type BigMomentKind =
  | "goal"
  | "penalty"
  | "var"
  | "red_card"
  | "comeback"
  | "extreme_pressure"
  | "last_minute"
  | "dramatic_draw";

export interface BigMoment {
  id: string;
  kind: BigMomentKind;
  emoji: string;
  headline: string;
  detail: string;
  intensity: number; // 0..1
  /** Optional contextual prediction question to fire alongside. */
  predictionQuestion?: string;
  createdAt: number;
}

export interface BigMomentSignals {
  minute: number;
  scoreHome: number;
  scoreAway: number;
  homeLabel: string;
  awayLabel: string;
  /** Optional: previous score snapshot — when omitted, engine tracks internally. */
  inDanger?: boolean;
  momentum?: number; // -1..1
  varActive?: boolean;
  redCard?: boolean;
  penalty?: boolean;
}

const EVT = "vs:big-moment";
const COOLDOWN: Record<BigMomentKind, number> = {
  goal: 8_000,
  penalty: 20_000,
  var: 15_000,
  red_card: 30_000,
  comeback: 25_000,
  extreme_pressure: 18_000,
  last_minute: 60_000,
  dramatic_draw: 30_000,
};

const lastByKind = new Map<BigMomentKind, number>();
let lastScoreHome = -1;
let lastScoreAway = -1;
let comebackArmed = false;

function gate(kind: BigMomentKind, now: number): boolean {
  const prev = lastByKind.get(kind) ?? 0;
  if (now - prev < COOLDOWN[kind]) return false;
  lastByKind.set(kind, now);
  return true;
}

export function resetBigMoments(): void {
  lastByKind.clear();
  lastScoreHome = -1;
  lastScoreAway = -1;
  comebackArmed = false;
}

export function detectBigMoment(s: BigMomentSignals, now: number = Date.now()): BigMoment | null {
  const home = s.homeLabel || "Local";
  const away = s.awayLabel || "Visitante";
  const diff = s.scoreHome - s.scoreAway;

  // Track score deltas
  const firstSeen = lastScoreHome === -1 && lastScoreAway === -1;
  const goalScored =
    !firstSeen && (s.scoreHome > lastScoreHome || s.scoreAway > lastScoreAway);
  const scorerLabel = s.scoreHome > lastScoreHome ? home : away;
  lastScoreHome = s.scoreHome;
  lastScoreAway = s.scoreAway;

  // 1. GOAL (highest priority)
  if (goalScored && gate("goal", now)) {
    // Arm comeback detection — losing team just scored to tie or lead.
    const becameTied = s.scoreHome === s.scoreAway;
    const becameLead =
      (scorerLabel === home && diff > 0 && diff <= 1) ||
      (scorerLabel === away && diff < 0 && diff >= -1);
    const isComeback = becameTied || (becameLead && comebackArmed);
    comebackArmed = becameTied;
    return emit({
      kind: isComeback ? "comeback" : "goal",
      emoji: isComeback ? "🔥" : "⚽",
      headline: isComeback ? `¡Comeback de ${scorerLabel}!` : `¡GOL de ${scorerLabel}!`,
      detail: `${s.scoreHome}-${s.scoreAway} · min ${s.minute}'`,
      intensity: isComeback ? 1 : 0.95,
      predictionQuestion: isComeback
        ? `¿${scorerLabel} da vuelta el partido?`
        : `¿${scorerLabel} marca otro en 10'?`,
      now,
    });
  }

  // 2. RED CARD
  if (s.redCard && gate("red_card", now)) {
    return emit({
      kind: "red_card",
      emoji: "🟥",
      headline: "¡Tarjeta roja!",
      detail: `Cambia el equilibrio · min ${s.minute}'`,
      intensity: 0.9,
      predictionQuestion: "¿El equipo con uno menos resiste?",
      now,
    });
  }

  // 3. PENALTY
  if (s.penalty && gate("penalty", now)) {
    return emit({
      kind: "penalty",
      emoji: "🎯",
      headline: "¡Penal señalado!",
      detail: `Momento decisivo · min ${s.minute}'`,
      intensity: 0.95,
      predictionQuestion: "¿Convierte el penal?",
      now,
    });
  }

  // 4. VAR
  if (s.varActive && gate("var", now)) {
    return emit({
      kind: "var",
      emoji: "📺",
      headline: "Revisión VAR en curso",
      detail: `Todo el estadio en pausa · min ${s.minute}'`,
      intensity: 0.8,
      predictionQuestion: "¿El VAR cambia la decisión?",
      now,
    });
  }

  // 5. LAST MINUTE DRAMA (≥88', diff ≤ 1)
  if (s.minute >= 88 && Math.abs(diff) <= 1 && gate("last_minute", now)) {
    return emit({
      kind: "last_minute",
      emoji: "⏱️",
      headline: "Minutos finales al rojo vivo",
      detail: `${s.scoreHome}-${s.scoreAway} · todo se define ahora`,
      intensity: 0.85,
      predictionQuestion: `¿Habrá gol antes del final?`,
      now,
    });
  }

  // 6. DRAMATIC DRAW (≥75', empate)
  if (s.minute >= 75 && diff === 0 && (s.scoreHome + s.scoreAway) >= 1 && gate("dramatic_draw", now)) {
    return emit({
      kind: "dramatic_draw",
      emoji: "⚖️",
      headline: "Empate dramático",
      detail: `${s.scoreHome}-${s.scoreAway} · tensión máxima`,
      intensity: 0.7,
      // PR-AUDIT: PredictionCard sólo soporta Sí/No.
      // Reformulado para mantener coherencia pregunta↔opciones.
      predictionQuestion: "¿Se rompe el empate antes del final?",
      now,
    });
  }

  // 7. EXTREME PRESSURE (danger + sustained momentum)
  if (
    s.inDanger &&
    Math.abs(s.momentum ?? 0) >= 0.6 &&
    gate("extreme_pressure", now)
  ) {
    const side = (s.momentum ?? 0) > 0 ? home : away;
    return emit({
      kind: "extreme_pressure",
      emoji: "🌋",
      headline: `${side} aprieta`,
      detail: `Presión extrema en el área · min ${s.minute}'`,
      intensity: 0.75,
      predictionQuestion: `¿${side} marca en los próximos 3'?`,
      now,
    });
  }

  return null;
}

function emit(args: {
  kind: BigMomentKind;
  emoji: string;
  headline: string;
  detail: string;
  intensity: number;
  predictionQuestion?: string;
  now: number;
}): BigMoment {
  const moment: BigMoment = {
    id: `${args.kind}_${args.now}`,
    kind: args.kind,
    emoji: args.emoji,
    headline: args.headline,
    detail: args.detail,
    intensity: args.intensity,
    predictionQuestion: args.predictionQuestion,
    createdAt: args.now,
  };
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent<BigMoment>(EVT, { detail: moment }));
  }
  void trackEvent("big_moment_detected", {
    kind: moment.kind,
    intensity: moment.intensity,
  });
  return moment;
}

export function onBigMoment(handler: (m: BigMoment) => void): () => void {
  if (typeof window === "undefined") return () => {};
  const fn = (e: Event) => handler((e as CustomEvent<BigMoment>).detail);
  window.addEventListener(EVT, fn);
  return () => window.removeEventListener(EVT, fn);
}